import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery } from '@apollo/client/react';
import { Box, Button, Center, Collapsible, Heading, HStack, IconButton, Spinner, Stack, Text, VStack, Field } from '@chakra-ui/react';
import { AutoResizeTextarea } from '@src/components/sco/AutoResizeTextarea';
import { MarkdownRenderer } from '@src/components/sco/MarkdownRenderer';
import { CheckIcon, MinusIcon } from '@src/components/sco/icons';
import { AddIcon } from '@src/components/sco/icons';
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider';
import { ContentTrack } from '@src/graphql-types';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { gql } from '@src/lib/gql';
import { darken } from 'color2k';
import { useRouter } from 'next/navigation';
import React, { useEffect, useState } from 'react';
type UserSprintSelectorProps = {
  afterSuccess?: () => any;
  submitTrigger?: number;
  hideHeader?: boolean;
};
export const UserSprintSelector = (props: UserSprintSelectorProps) => {
  const {
    getBreakpointValue
  } = useBreakpointContext();
  const {
    currentUser
  } = useCurrentUser();
  const [activeSprintSlug, setActiveSprintSlug] = useState(currentUser?.activeSprintSlug);
  const [activeSprintNotes, setActiveSprintNotes] = useState(currentUser?.activeSprintNotes);
  const router = useRouter();
  const sprintsQuery = useQuery(gql`
    query {
      contentTracks(where: { type_in: [Sprint] }) {
        id
        title
        type
        slug
        shortDescription
        longDescription
        color {
          hex
        }
      }
    }
  `);
  const [updateUserActiveSprint, updateUserActiveSprintMutation] = useMutation(gql`
    mutation ($input: UpdateUserActiveSprintInput!) {
      updateUserActiveSprint(input: $input) {
        user {
          id
          activeSprintSlug
          activeSprintNotes
          founderNeedsKeys
          founderNeedsOther
        }
      }
    }
  `);
  useEffect(() => {
    if (props.submitTrigger) {
      onSubmit();
    }
  }, [props.submitTrigger]);
  useEffect(() => {
    if (currentUser.activeSprintSlug) {
      setActiveSprintSlug(currentUser.activeSprintSlug);
    }
    if (currentUser.activeSprintNotes) {
      setActiveSprintNotes(currentUser.activeSprintNotes);
    }
  }, [currentUser.activeSprintSlug, currentUser.activeSprintNotes]);
  const onSubmit = async () => {
    try {
      await updateUserActiveSprint({
        variables: {
          input: {
            activeSprintSlug,
            activeSprintNotes
          }
        }
      });
    } catch (err) {
      alert('Your selection could not be saved.');
    }
    if (props.afterSuccess) {
      props.afterSuccess();
    } else {
      alert('Your Sprint has been saved.');
    }
  };
  if (sprintsQuery.error) {
    return <></>;
  }
  return <>
      {!props.hideHeader && <Box padding={'1em 0'}>
          <Heading size={'2xl'}>What is your top goal or challenge right now?</Heading>
          <Text fontSize={getBreakpointValue(['md', 'md', 'lg'])} marginY={'0.5em'}>
            Pick ONE of our Startup Sprints so we can help you focus on achieving your goal. You can change
            this after you crush this first goal, or if your goals change dramatically.
          </Text>
        </Box>}
      <VStack align={'stretch'} gap={'1em'} width={'full'}>
        {sprintsQuery.loading && <Center padding={'1em'}>
            <Spinner />
          </Center>}
        {sprintsQuery.data?.contentTracks && sprintsQuery.data?.contentTracks.map((sprint: ContentTrack) => <SprintCard activeSprintNotes={activeSprintNotes} activeSprintSlug={activeSprintSlug} sprint={sprint} onSubmit={onSubmit} onChange={({
        sprint: sprint_0,
        notes
      }) => {
        if (sprint_0) {
          setActiveSprintSlug(sprint_0?.slug);
        }
        if (notes) {
          setActiveSprintNotes(notes);
        }
      }} />)}
      </VStack>
    </>;
};
const SprintCard = props => {
  const $ = _c(63);
  const isSelected = props.sprint.slug == props.activeSprintSlug;
  const {
    getBreakpointValue
  } = useBreakpointContext();
  const [isExpanded, setIsExpanded] = useState(isSelected);
  let t0;
  let t1;
  if ($[0] !== isSelected) {
    t0 = () => {
      if (isSelected) {
        setIsExpanded(isSelected);
      } else {
        setIsExpanded(false);
      }
    };
    t1 = [isSelected];
    $[0] = isSelected;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  const {
    sprint
  } = props;
  const t2 = isSelected ? sprint.color.hex : "whiteAlpha.100";
  const t3 = props.activeSprintSlug && !isSelected ? 0.5 : 1;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      opacity: 1
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== isExpanded || $[5] !== isSelected || $[6] !== props.activeSprintSlug || $[7] !== sprint.color) {
    t5 = !props.activeSprintSlug || isExpanded || isSelected ? darken(sprint.color.hex, isExpanded ? 0.1 : 0.2) : "whiteAlpha.100";
    $[4] = isExpanded;
    $[5] = isSelected;
    $[6] = props.activeSprintSlug;
    $[7] = sprint.color;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== isExpanded || $[10] !== isSelected) {
    t6 = () => {
      if (!isSelected) {
        setIsExpanded(!isExpanded);
      }
    };
    $[9] = isExpanded;
    $[10] = isSelected;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== getBreakpointValue) {
    t7 = getBreakpointValue([""]);
    $[12] = getBreakpointValue;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const t8 = isExpanded ? `Collapse ${sprint.title}` : `Expand ${sprint.title}`;
  let t10;
  let t9;
  if ($[14] !== isExpanded || $[15] !== isSelected) {
    t9 = () => !isSelected && setIsExpanded(!isExpanded);
    t10 = isExpanded ? isSelected ? <CheckIcon /> : <MinusIcon /> : <AddIcon />;
    $[14] = isExpanded;
    $[15] = isSelected;
    $[16] = t10;
    $[17] = t9;
  } else {
    t10 = $[16];
    t9 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t8 || $[20] !== t9) {
    t11 = <IconButton aria-label={t8} variant="outline" onClick={t9}>{t10}</IconButton>;
    $[18] = t10;
    $[19] = t8;
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== sprint.title) {
    t12 = <Heading flexGrow={1} size="md">{sprint.title}</Heading>;
    $[22] = sprint.title;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== sprint.shortDescription) {
    t13 = <Text>{sprint.shortDescription}</Text>;
    $[24] = sprint.shortDescription;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t12 || $[27] !== t13) {
    t14 = <Box width="full">{t12}{t13}</Box>;
    $[26] = t12;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== isSelected || $[30] !== props || $[31] !== sprint) {
    t15 = isSelected ? <Button borderColor="colorPalette.fg" variant="outline" _hover={{
      borderColor: "white"
    }} onClick={e => {
      e.preventDefault();
      e.stopPropagation();
      props.onChange({
        sprint
      });
    }}><CheckIcon />Selected</Button> : <Button variant="cta" onClick={e_0 => {
      e_0.preventDefault();
      e_0.stopPropagation();
      props.onChange({
        sprint
      });
    }}>Select Sprint</Button>;
    $[29] = isSelected;
    $[30] = props;
    $[31] = sprint;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t14 || $[34] !== t15) {
    t16 = <HStack width="full">{t14}{t15}</HStack>;
    $[33] = t14;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      li: {
        color: "whiteAlpha.900"
      }
    };
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== sprint.longDescription) {
    t18 = <Box paddingY="1em"><MarkdownRenderer typographyStyles={t17}>{sprint.longDescription}</MarkdownRenderer></Box>;
    $[37] = sprint.longDescription;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== isSelected || $[40] !== props) {
    t19 = isSelected && <Box bgColor="blackAlpha.600" borderRadius="md" padding="1.5em 1em"><Field.Root><Field.Label>Tell us the key outcome you are hoping to achieve so we can help you nail it.</Field.Label><AutoResizeTextarea rows={3} value={props.activeSprintNotes} onChange={ev => {
          props.onChange({
            notes: ev.target.value
          });
        }} /><Box paddingTop="1em" textAlign="right"><Button variant="cta" onClick={props.onSubmit}>Save</Button></Box></Field.Root></Box>;
    $[39] = isSelected;
    $[40] = props;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== t18 || $[43] !== t19) {
    t20 = <Collapsible.Content>{t18}{t19}</Collapsible.Content>;
    $[42] = t18;
    $[43] = t19;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== isExpanded || $[46] !== t20) {
    t21 = <Collapsible.Root open={isExpanded}>{t20}</Collapsible.Root>;
    $[45] = isExpanded;
    $[46] = t20;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== t16 || $[49] !== t21) {
    t22 = <Box flexGrow={1} width="full">{t16}{t21}</Box>;
    $[48] = t16;
    $[49] = t21;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] !== t11 || $[52] !== t22) {
    t23 = <HStack align="flex-start" gap="1em" justify="stretch" width="full">{t11}{t22}</HStack>;
    $[51] = t11;
    $[52] = t22;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] !== t23 || $[55] !== t7) {
    t24 = <Stack align="flex-start" direction={t7}>{t23}</Stack>;
    $[54] = t23;
    $[55] = t7;
    $[56] = t24;
  } else {
    t24 = $[56];
  }
  let t25;
  if ($[57] !== t2 || $[58] !== t24 || $[59] !== t3 || $[60] !== t5 || $[61] !== t6) {
    t25 = <Box border="1px solid" borderColor={t2} borderRadius="lg" boxShadow="lg" opacity={t3} padding="1em" _hover={t4} bgColor={t5} onClick={t6}>{t24}</Box>;
    $[57] = t2;
    $[58] = t24;
    $[59] = t3;
    $[60] = t5;
    $[61] = t6;
    $[62] = t25;
  } else {
    t25 = $[62];
  }
  return t25;
};